/**
 * «Мессенджер → Действия»: здоровье сбора по источникам, сотрудники, лента с
 * сырым JSON. Спека `docs/specs/2026-09-25-messenger-actions-design.md`; числа —
 * те же, что у `prokat-runner messenger-actions`.
 */
import { useQuery } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router";

import {
  type ActionUser,
  type Category,
  dt,
  type FeedItem,
  type Health,
  loadActionRaw,
  loadMessengerActions,
  type MessengerActions as Data,
  rhythm,
} from "@/lib/messenger";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
const th = "px-3 py-1.5 font-medium";
const num = "px-3 py-1.5 text-right tabular-nums";
const select = "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg";

const CATEGORY: Record<Category, string> = { send: "отправка", new: "новое", archive: "архив", other: "прочее" };
const FIELD: { key: keyof Health["filled"]; label: string; hint: string }[] = [
  { key: "account", label: "Аккаунт", hint: "messengerAccountID" },
  { key: "recipient", label: "Получатель", hint: "messengerID" },
  { key: "chat", label: "Чат", hint: "messengerChatID" },
  { key: "msg", label: "Сообщение", hint: "messengerMsgID — связь с sMessenger_msg" },
];

function HealthTable({ r }: { r: Data }) {
  return (
    <section className={card} data-annot="здоровье сбора">
      <h2 className="border-b border-border px-4 py-3 text-[13px] font-medium">
        Здоровье сбора · записей {r.totals.records} · уникальных отправок {r.totals.sends} · дублей {r.totals.dups}
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className={th}>Источник</th>
              <th className={`${th} text-right`}>Записей</th>
              <th className={`${th} text-right`}>Отпр.</th>
              <th className={`${th} text-right`}>Новое</th>
              <th className={`${th} text-right`}>Архив</th>
              {FIELD.map((f) => (
                <th key={f.key} className={`${th} text-right`} title={f.hint}>
                  {f.label}
                </th>
              ))}
              <th className={`${th} text-right`} title="сырые пакеты Messenger Viewer: ок / ошибка">
                Пакеты
              </th>
              <th className={th}>Последняя · ритм</th>
            </tr>
          </thead>
          <tbody>
            {r.health.map((h) => (
              <tr
                key={h.source}
                className={`border-t border-border ${h.silent ? "bg-err/10" : ""}`}
                data-annot={`источник ${h.source}`}
              >
                <td className={`px-3 py-1.5 ${h.silent ? "font-medium text-err" : h.total ? "" : "text-fg-muted"}`}>
                  {h.source}
                </td>
                <td className={num}>{h.total}</td>
                <td className={num}>
                  {h.sends}
                  {h.dups ? <span className="text-fg-faint" title="дубли записи CRM"> −{h.dups}</span> : null}
                </td>
                <td className={num}>{h.records.new}</td>
                <td className={num}>{h.records.archive}</td>
                {FIELD.map((f) => {
                  const zero = h.zero.includes(f.key);
                  return (
                    <td
                      key={f.key}
                      className={`${num} ${zero ? "font-medium text-err" : ""}`}
                      title={zero ? "не заполняется" : undefined}
                    >
                      {h.total ? (
                        <>
                          {h.filled[f.key]}
                          <span className={zero ? "" : "text-fg-faint"}>/{h.total}</span>
                        </>
                      ) : (
                        <span className="text-fg-faint">—</span>
                      )}
                    </td>
                  );
                })}
                <td className={num}>
                  {h.packets.ok}
                  {h.packets.error ? <span className="text-err">/{h.packets.error}</span> : null}
                </td>
                <td className={`whitespace-nowrap px-3 py-1.5 ${h.silent ? "text-err" : "text-fg-muted"}`}>
                  {h.last_at ? `${h.silent ? "нет данных с " : ""}${dt(h.last_at)}` : "> 90 дн"} · {rhythm(h)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11.5px] text-fg-faint">
        Робот не считается. Красным — поле пустое у всех записей источника (от 10 за день) или регулярный источник молчит
        больше суток. Связь с сообщением, чат и архив JSON дописывает крон CRM messengerAction.php — по данным он стоит с
        04.07.2026 (общий план, находка 2).
      </div>
    </section>
  );
}

function Users({ r }: { r: Data }) {
  return (
    <section className={card} data-annot="сотрудники">
      <h2 className="border-b border-border px-4 py-3 text-[13px] font-medium">Сотрудники: {r.users.length}</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className={th}>Сотрудник</th>
              <th className={`${th} text-right`}>Отправок</th>
              <th className={`${th} text-right`}>Записей</th>
              <th className={th}>По источникам: отпр/нов/арх</th>
            </tr>
          </thead>
          <tbody>
            {r.users.map((u) => (
              <tr key={u.id ?? 0} className="border-t border-border hover:bg-bg-hover">
                <td className="whitespace-nowrap px-3 py-1.5">
                  {u.id ? (
                    <Link to={`/activity/${u.id}?date=${r.date}`} className="hover:underline">
                      {u.name}
                    </Link>
                  ) : (
                    u.name
                  )}
                </td>
                <td className={num}>{u.sends}</td>
                <td className={num}>{u.total}</td>
                <td className="px-3 py-1.5 text-fg-muted">
                  {Object.entries(u.sources)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([s, v]) => `${s} ${v.sends}/${v.new}/${v.archive}`)
                    .join(" · ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Raw({ id }: { id: number }) {
  const q = useQuery({ queryKey: ["action-raw", id], queryFn: () => loadActionRaw(id) });
  if (q.isPending) return <div className="text-fg-muted">Читаю запись…</div>;
  if (q.isError) return <div className="text-err">Не удалось: {String(q.error.message)}</div>;
  if (q.data.archived) return <div className="text-fg-muted">JSON в архиве CRM (файл на сервере), в базе его нет.</div>;
  return (
    <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-all text-[11.5px] text-fg-muted">
      {JSON.stringify(q.data.data, null, 1)}
    </pre>
  );
}

function Feed({ r }: { r: Data }) {
  const [source, setSource] = useState("");
  const [user, setUser] = useState("");
  const [category, setCategory] = useState("");
  const [hideDups, setHideDups] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const names = useMemo(() => new Map(r.users.map((u: ActionUser) => [u.id, u.name])), [r.users]);
  const rows = r.feed.filter(
    (f: FeedItem) =>
      (!source || f.source === source) &&
      (!user || String(f.user_id ?? "") === user) &&
      (!category || f.category === category) &&
      !(hideDups && f.dup),
  );

  return (
    <section className={card} data-annot="лента действий">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        <h2 className="mr-2 text-[13px] font-medium">
          Лента: {rows.length}
          {rows.length !== r.feed.length ? <span className="text-fg-faint"> из {r.feed.length}</span> : null}
        </h2>
        <select value={source} onChange={(e) => setSource(e.target.value)} className={select}>
          <option value="">все источники</option>
          {r.health
            .filter((h) => h.total)
            .map((h) => (
              <option key={h.source} value={h.source}>
                {h.source}
              </option>
            ))}
        </select>
        <select value={user} onChange={(e) => setUser(e.target.value)} className={select}>
          <option value="">все сотрудники</option>
          {r.users.map((u) => (
            <option key={u.id ?? 0} value={String(u.id ?? "")}>
              {u.name}
            </option>
          ))}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={select}>
          <option value="">все категории</option>
          {Object.entries(CATEGORY).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-[12.5px] text-fg-muted">
          <input type="checkbox" checked={hideDups} onChange={(e) => setHideDups(e.target.checked)} />
          скрыть дубли
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className={th}>Время</th>
              <th className={th}>Сотрудник</th>
              <th className={th}>Источник</th>
              <th className={th}>Действие</th>
              <th className={th}>Аккаунт</th>
              <th className={th}>Получатель</th>
              <th className={th}>Текст</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <Fragment key={f.id}>
                <tr
                  onClick={() => setOpen(open === f.id ? null : f.id)}
                  className={`cursor-pointer border-t border-border align-top hover:bg-bg-hover ${f.dup ? "text-fg-faint" : ""} ${open === f.id ? "bg-bg-hover" : ""}`}
                  title="сырой JSON запроса"
                >
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{f.at.slice(11, 19)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">{names.get(f.user_id) ?? "—"}</td>
                  <td className="px-3 py-1.5">{f.source}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    {CATEGORY[f.category]}
                    {f.dup ? <span className="ml-1 text-[11px]">дубль</span> : null}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">{f.account ?? <span className="text-fg-faint">—</span>}</td>
                  <td className="px-3 py-1.5">
                    {f.to ?? <span className="text-fg-faint">—</span>}
                    {f.phone ? <div className="font-mono text-[11.5px] text-fg-muted" title="телефон из CRM sPhone">{f.phone}</div> : null}
                  </td>
                  <td className="px-3 py-1.5">{f.text ?? <span className="text-fg-faint">—</span>}</td>
                </tr>
                {open === f.id ? (
                  <tr className="border-t border-border bg-bg">
                    <td colSpan={7} className="px-4 py-2">
                      <div className="mb-1 text-[11.5px] text-fg-faint">
                        sMessenger_action.id {f.id} · чат {f.chat_id ?? "—"} · сообщение {f.msg_id ?? "—"}
                      </div>
                      <Raw id={f.id} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function MessengerActions({ day }: { day: string }) {
  const q = useQuery({ queryKey: ["messenger-actions", day], queryFn: () => loadMessengerActions(day) });
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Читаю действия из CRM…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">Не удалось получить данные: {String(q.error.message)}</div>;
  return (
    <>
      <HealthTable r={q.data} />
      <Users r={q.data} />
      <Feed r={q.data} />
    </>
  );
}
